import CoreAnalytics from './coreAnalytics';

/** @typedef {import('./analyticsTypes').ClientDeletedEventPayload} ClientDeletedEventPayload */
/** @typedef {import('./analyticsTypes').ClientDeleteBlockedEventPayload} ClientDeleteBlockedEventPayload */
/** @typedef {import('./analyticsTypes').ClientDeleteFailedEventPayload} ClientDeleteFailedEventPayload */
/** @typedef {import('./analyticsTypes').DeletedClientsViewedEventPayload} DeletedClientsViewedEventPayload */
/** @typedef {import('./analyticsTypes').ClientRestoredEventPayload} ClientRestoredEventPayload */

/** @typedef {import('./analyticsTypes').ClientRestoreFailedEventPayload} ClientRestoreFailedEventPayload */

/**
 * Analytics for deleting clients and restoring soft-deleted ones from the Clients page.
 * Every method picks its props by name, so passing a whole client never sends its name or other fields.
 */
class ClientLifecycleAnalytics extends CoreAnalytics {
  EVENT_NAMES = {
    CLIENT_DELETED: 'client_deleted',
    CLIENT_DELETE_BLOCKED: 'client_delete_blocked',
    CLIENT_DELETE_FAILED: 'client_delete_failed',
    DELETED_CLIENTS_VIEWED: 'deleted_clients_viewed',
    CLIENT_RESTORED: 'client_restored',
    CLIENT_RESTORE_FAILED: 'client_restore_failed',
  };

  DELETE_BLOCKED_REASONS = {
    SESSION_IN_PROGRESS: 'session_in_progress',
  };

  RESTORE_FAILED_REASONS = {
    NOT_RESTORABLE: 'not_restorable',
    UNEXPECTED: 'unexpected',
  };

  /**
   * Fires once the delete write resolves: a soft delete, or a hard delete at an integrated clinic.
   *
   * @param {ClientDeletedEventPayload} payload
   */
  trackClientDeleted({ targetClientId, clientStatus, isPermanent }) {
    if (!targetClientId) {
      return;
    }

    this.track(this.EVENT_NAMES.CLIENT_DELETED, {
      targetClientId, clientStatus, isPermanent, eventVersion: 1,
    });
  } // trackClientDeleted()

  /**
   * Fires when a pre-delete check stops the delete, e.g. the client has a running session.
   *
   * @param {ClientDeleteBlockedEventPayload} payload
   */
  trackClientDeleteBlocked({ targetClientId, reason }) {
    if (!targetClientId) {
      return;
    }

    this.track(this.EVENT_NAMES.CLIENT_DELETE_BLOCKED, { targetClientId, reason, eventVersion: 1 });
  } // trackClientDeleteBlocked()

  /**
   * Fires when the delete throws before it was written, so the client is still there.
   *
   * @param {ClientDeleteFailedEventPayload} payload
   */
  trackClientDeleteFailed({ targetClientId, isPermanent, errorCode }) {
    if (!targetClientId) {
      return;
    }

    this.track(this.EVENT_NAMES.CLIENT_DELETE_FAILED, {
      targetClientId, isPermanent, errorCode: errorCode || 'unknown', eventVersion: 1,
    });
  } // trackClientDeleteFailed()

  /**
   * Fires when the Restore clients dialog opens.
   *
   * @param {DeletedClientsViewedEventPayload} payload
   */
  trackDeletedClientsViewed({ restorableCount }) {
    this.track(this.EVENT_NAMES.DELETED_CLIENTS_VIEWED, { restorableCount, eventVersion: 1 });
  } // trackDeletedClientsViewed()

  /**
   * Fires when the restoreClient callable resolves, even if the license recount after it fails.
   *
   * @param {ClientRestoredEventPayload} payload
   */
  trackClientRestored({
    targetClientId, daysSinceDeleted, restoredByDeleter, clientStatus,
  }) {
    if (!targetClientId) {
      return;
    }

    this.track(this.EVENT_NAMES.CLIENT_RESTORED, {
      targetClientId, daysSinceDeleted, restoredByDeleter, clientStatus, eventVersion: 1,
    });
  } // trackClientRestored()

  /**
   * Fires when the restoreClient callable throws.
   *
   * @param {ClientRestoreFailedEventPayload} payload
   */
  trackClientRestoreFailed({ targetClientId, reason }) {
    if (!targetClientId) {
      return;
    }

    this.track(this.EVENT_NAMES.CLIENT_RESTORE_FAILED, { targetClientId, reason, eventVersion: 1 });
  } // trackClientRestoreFailed()
}

export default new ClientLifecycleAnalytics();
